/* eslint-disable jsx-a11y/anchor-is-valid */

import { useLayoutEffect, useRef, useState } from "react";
import { Button, OverlayTrigger } from "react-bootstrap";
import { Tooltip } from "react-bootstrap";
import SVG from "react-inlinesvg";
import "../ListsWidget.scss";
import { useGetPendingActions } from "../../../../../swrHelpers/dashboard";
import { toAbsoluteUrl } from "../../../../_helpers";
import { decryptedData, getBaseImageURL } from "../../../../../helpers";
import { redirect, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import {
  CANCEL_REQUEST_ACTION_TYPE,
  getCancelRequestSummary,
  getCancelRequestNavigation,
  getCancelInvestmentNavigation,
} from "../../../../../helpers/cancellationRequestPendingAction";

export function PendingActions({ className }) {
  const [selectedBtn, setSelectedBtn] = useState("high");
  const navigate = useNavigate();
  const intl = useIntl();
  const { taxonomy } = useSelector((state) => state?.taxonomy);
  const { generalSettings } = useSelector((state) => state.settings);
  const globalImagePath = useRef(getBaseImageURL());

  const { data, isValidating } = useGetPendingActions(1, selectedBtn);

  const highCount = data?.highCount || 0;
  const moderateCount = data?.moderateCount || 0;
  const lowCount = data?.lowCount || 0;
  const priorityRef = useRef(null);
  const handlePriorityChange = (priority) => {
    setSelectedBtn(priority);
  };

  const actionTypes = [
    {
      label: "Campaign Approval",
      value: "campaign",
      path: `${taxonomy?.projectSingular} Management > ${taxonomy?.projectPlural}`,
    },
    {
      label: "Accreditation Approval",
      value: "accreditation",
      path: "User Management > Accreditation Users",
    },
    {
      label: "Investment",
      value: "investment",
      path: "User Management > Accreditation Users",
    },
    {
      label: "Wallet Topup",
      value: "walletTopup",
      path: "Wallet > Wallet Transaction",
    },
    {
      label: "Wallet Withdraw Approval",
      value: "walletWithdraw",
      path: "Wallet > Wallet Transaction",
    },
    {
      label: "Repayment Request",
      value: "repaymentRequest",
      path: `${taxonomy?.projectSingular} Management > ${taxonomy?.projectPlural}`,
    },
    {
      label: "Payout Request",
      value: "payoutRequest",
      path: `${taxonomy?.projectSingular} Management > ${taxonomy?.projectPlural}`,
    },
    {
      label: "Issuer Account Verification",
      value: "issuerVerification",
      path: "User Management > Issuer Verification",
    },
    {
      label: "Extension Request",
      value: "extensionRequest",
      path: `${taxonomy?.projectSingular} Management > ${taxonomy?.projectPlural}`,
    },
    {
      label: "Investment Cancellation – Manual Repayment Required",
      value: "cancelInvestment",
      path: `${taxonomy?.projectSingular} Management > ${taxonomy?.projectPlural}`,
    },
    {
      label: intl.formatMessage({ id: "INVESTMENT.CANCEL.PENDING.ACTION" }),
      value: CANCEL_REQUEST_ACTION_TYPE,
      path: `${taxonomy?.projectSingular} Management > ${taxonomy?.projectPlural} > Investor`,
    },
  ];

  const displayLoadMore = data?.displayLoadMore ?? false;

  const pendingActionsData =
    data?.pendingActions?.length > 10
      ? data?.pendingActions.slice(0, 5)
      : data?.pendingActions;

  function getRelativeTime(createdAt) {
    const now = Date.now();
    const diff = now - createdAt;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (seconds < 60) return `${seconds} seconds ago`;
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
    if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  }

  function viewAll() {
    navigate("/dashboard/pending-actions");
  }

  const handleNavigation = (item) => {
    if (item?.type === "accreditation") {
      navigate("/user-management/accreditation-detail/" + item?.id);
    } else if (item?.type === "walletTopup") {
      navigate("/wallet/topup-requests");
    } else if (item?.type === "walletWithdraw") {
      navigate("/wallet/withdraw-requests");
    } else if (item?.type === "campaign" || item?.type === "payoutRequest" || item?.type === "extensionRequest") {
      navigate("/campaign/campaign-details/" + item?.id);
    } else if (item?.type === "repaymentRequest") {
      navigate(
        "/campaign/campaign-details/" + item?.companyDetails?.campaignId
      );
    } else if (item?.type === "investment") {
      navigate(
        "/user-management/campaign/campaign-details/" +
          item?.companyId?.campaignId
      );
    } else if (item?.type === "cancelInvestment") {
      const { path, options } = getCancelInvestmentNavigation(item);
      navigate(path, options);
    } else if (item?.type === "issuerVerification") {
      navigate("/user-management/issuer-verification-detail/" + item?.id);
    } else if (item?.type === CANCEL_REQUEST_ACTION_TYPE) {
      const { path, options } = getCancelRequestNavigation(item);
      navigate(path, options);
    }
  };

  useLayoutEffect(() => {
    const activeBtn = priorityRef.current?.querySelector(
      ".priority-btn-active"
    );

    if (activeBtn && priorityRef.current) {
      const { offsetLeft, offsetWidth } = activeBtn;

      priorityRef.current.style.setProperty("--glider-left", `${offsetLeft}px`);
      priorityRef.current.style.setProperty(
        "--glider-width",
        `${offsetWidth}px`
      );
    }
  }, [selectedBtn]);

  return (
    <>
      <div className={`card card-custom ${className}`}>
        {/* Header */}
        <div className="pending-actions-card-header d-flex justify-content-between align-items-center border-0 px-4 py-5">
          <h3 className="fw-bold h5 d-flex align-items-center gap-2 text-dark mb-0">
            Pending Actions
            <OverlayTrigger
              overlay={
                <Tooltip id="pending-actions-tooltip">
                  All pending actions/tasks sorted by priority to help manage
                  them on time.
                </Tooltip>
              }>
              <span className="tooltip-wrapper">
                <i className="fa fa-question-circle" />
              </span>
            </OverlayTrigger>
          </h3>
          {displayLoadMore && (
            <button
              type="button"
              className="view-all-btn fw-bold"
              onClick={() => {
                viewAll();
              }}>
              View All
            </button>
          )}
        </div>
        {/* Body */}
        <div className="card-body py-0 activities-wrapper thin-scrollbar px-5">
          <div
            ref={priorityRef}
            className="priority-btn-wrapper glider-wrapper py-1 mb-5">
            <span className="priority-glider" />
            <Button
              onClick={() => handlePriorityChange("high")}
              className={`priority-btn rounded-xl border-0 ${
                selectedBtn === "high"
                  ? "text-black bg-white priority-btn-active"
                  : ""
              }`}>
              High
              <span
                style={{
                  backgroundColor:
                    selectedBtn === "high" ? "#FF1B33" : "#FF1B3326",
                  color: selectedBtn === "high" ? "#fff" : "#FF1B33",
                }}>
                {highCount}
              </span>
            </Button>
            <Button
              onClick={() => handlePriorityChange("moderate")}
              className={`priority-btn rounded-xl border-0 ${
                selectedBtn === "moderate"
                  ? "text-black bg-white priority-btn-active"
                  : ""
              }`}>
              Moderate
              <span
                style={{
                  backgroundColor:
                    selectedBtn === "moderate" ? "#DB9A00" : "#DB9A0026",
                  color: selectedBtn === "moderate" ? "#fff" : "#DB9A00",
                }}>
                {moderateCount}
              </span>
            </Button>
            <Button
              onClick={() => handlePriorityChange("low")}
              className={`priority-btn rounded-xl border-0 ${
                selectedBtn === "low"
                  ? "text-black bg-white priority-btn-active"
                  : ""
              }`}>
              Low
              <span
                style={{
                  backgroundColor:
                    selectedBtn === "low" ? "#006F25" : "#006F2526",
                  color: selectedBtn === "low" ? "#fff" : "#006F25",
                }}>
                {lowCount}
              </span>
            </Button>
          </div>
          <div
            className={
              pendingActionsData?.length === 0
                ? `d-flex align-items-center justify-content-center`
                : ""
            }>
            {isValidating ? (
              <div className="text-primary">
                <p className="text-center">Loading...</p>
              </div>
            ) : pendingActionsData?.length > 0 ? (
              pendingActionsData?.map((data) => {
                return (
                  <div
                    key={data?.id}
                    className="w-100 activity-card d-flex align-items-center px-3 py-2 rounded-fix mb-3">
                    <div
                      className="me-5 flex-shrink-0"
                      style={{
                        width: "40px",
                        height: "40px",
                      }}>
                      <img
                        className="w-100 h-100 rounded-circle"
                        style={{ width: "40px" }}
                        alt="user"
                        src={
                          globalImagePath.current +
                          (data?.type === "walletWithdraw" ||
                          data?.type === "walletTopup"
                            ? data?.userId?.photo
                            : data?.type === "campaign" ||
                              data?.type === "payoutRequest"
                            ? data?.companyLogo
                            : data?.type === "accreditation"
                            ? data?.photo
                            : data?.type === "repaymentRequest"
                            ? data?.companyDetails?.companyLogo
                            : data?.type === "investment" ||
                              data?.type === "cancelInvestment" ||
                              data?.type === CANCEL_REQUEST_ACTION_TYPE
                            ? data?.companyId?.companyLogo
                            : "")
                        }
                        onError={(e) => {
                          e.target.onerror = null; // prevent infinite loop if fallback fails
                          e.target.src = toAbsoluteUrl(
                            "media/users/default.jpg"
                          );
                        }}
                      />
                    </div>
                    <div className="d-flex flex-column">
                      <div className="fw-bold activity-title">
                        {actionTypes?.map((actionType) => {
                          if (actionType.value === data?.type) {
                            if (
                              data?.type === "walletTopup" &&
                              data?.gatewayId?.paymentType === "offline"
                            ) {
                              return `${actionType.label} Request `;
                            } else {
                              return actionType.label;
                            }
                          }
                        })}{" "}
                        :{" "}
                        {data?.type === "walletWithdraw" ||
                        data?.type === "walletTopup"
                          ? ` ${decryptedData(
                              data?.userId?.firstName
                            )} ${decryptedData(data?.userId?.lastName)}`
                          : data?.type === "campaign" ||
                            data?.type === "payoutRequest"
                          ? decryptedData(data?.companyName)
                          : data?.type === "accreditation"
                          ? ` ${decryptedData(data?.firstName)} ${decryptedData(
                              data?.lastName
                            )}`
                          : data?.type === "repaymentRequest"
                          ? decryptedData(data?.companyDetails?.companyName)
                          : data?.type === "cancelInvestment"
                          ? `${decryptedData(
                              data?.userId?.firstName
                            )} ${decryptedData(data?.userId?.lastName)} (${decryptedData(
                              data?.companyId?.companyName
                            )})`
                          : data?.type === CANCEL_REQUEST_ACTION_TYPE
                          ? getCancelRequestSummary(data, intl, generalSettings)
                          : data?.type === "investment"
                          ? decryptedData(data?.companyId?.companyName)
                          : ""}
                      </div>
                      <div className="d-md-flex pt-md-0 pt-3">
                        <span className="text-muted d-block activity-time">
                          {getRelativeTime(data?.createdAt)}
                        </span>
                        <ul className="activity-path ps-0 ps-md-6">
                          {actionTypes?.map((actionType, index) => {
                            if (actionType?.value === data?.type) {
                              return <li key={index}>{actionType?.path}</li>;
                            }
                          })}
                        </ul>
                      </div>
                    </div>
                    <div className="ms-auto cursor-pointer">
                      <button
                        className="border-0 rounded-fix"
                        onClick={() => handleNavigation(data)}>
                        <SVG
                          src={toAbsoluteUrl(
                            "media/svg/icons/NewDashboard/send-icon.svg"
                          )}></SVG>
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <span className="text-center">
                <img
                  height={120}
                  src={toAbsoluteUrl("media/animations/nodata-animation.gif")}
                  alt="no data found!"
                />
                <br />
                <span className="text-primary fs-6 fw-bold">
                  No Data Available!
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
